import { ObjectTools } from '@sol/components/tools/object-tools';
import { SolIcon } from '@sol/icons';
import { SolApp, SolMenuGroup, SolNavbarItem, SolRouteId } from '@sol/components';
import { MenuTools } from '@sol/components/tools/menu-tools';
import { StringTools } from '@sol/components/tools/string-tools';

export class SolNavbarState {
  private constructor(
    public readonly name: string,
    public readonly menu: SolNavbarItem[],
    public readonly system: SolNavbarItem[],
    public readonly account: SolNavbarItem[],
    public readonly activeRouteIds: SolRouteId[],
    public readonly navbarVisible: boolean,
    public readonly expandedIds: SolRouteId[],
    public readonly app: SolApp | undefined,
    public readonly appsVisible: boolean,
    public readonly appsDropdownVisible: boolean,
    public readonly appHover: boolean,
    public readonly loading: boolean,
    public readonly initials: string,
    public readonly appDisplayName: string,
    public readonly appDisplayIcon: SolIcon | undefined,
    public readonly appMenu: SolMenuGroup[],
    public readonly accountMenu: SolMenuGroup[],
    public readonly systemMenus: Map<string, SolMenuGroup[]>,
    public readonly hostClasses: string,
  ) {}

  public static of(): SolNavbarState {
    return new SolNavbarState(
      '',
      [],
      [],
      [],
      [],
      true,
      [],
      undefined,
      false,
      false,
      false,
      true,
      '',
      '',
      undefined,
      [],
      [],
      new Map<string, SolMenuGroup[]>(),
      '',
    ).recalculateHostClasses();
  }

  public withNavigation(
    app: SolApp | undefined,
    appMenu: SolMenuGroup[],
    appsVisible: boolean,
    appsDropdownVisible: boolean,
    appDisplayName: string,
    appDisplayIcon: SolIcon | undefined,
    menu: SolNavbarItem[],
    system: SolNavbarItem[],
    account: SolNavbarItem[],
    navbarVisible: boolean,
    loading: boolean,
  ): SolNavbarState {
    return this.copy({
      app,
      appMenu,
      appsVisible,
      appsDropdownVisible,
      appDisplayName,
      appDisplayIcon,
      menu,
      system,
      account,
      navbarVisible,
      loading,
    })
      .recalculateHostClasses()
      .recalculatePopoverMenus();
  }

  public withActiveNavbarItems(activeRouteIds: SolRouteId[]): SolNavbarState {
    return this.copy({ activeRouteIds, expandedIds: activeRouteIds });
  }

  public withName(name: string): SolNavbarState {
    const initials = StringTools.toInitials(name);
    return this.copy({ name, initials });
  }

  public withExpandedIds(expandedIds: SolRouteId[]): SolNavbarState {
    return this.copy({ expandedIds });
  }

  public withAppHover(appHover: boolean): SolNavbarState {
    return this.copy({ appHover });
  }

  public withLoading(loading: boolean): SolNavbarState {
    return this.copy({ loading });
  }

  public withNavbarVisible(navbarVisible: boolean): SolNavbarState {
    return this.copy({ navbarVisible }).recalculateHostClasses();
  }

  public withInitials(initials: string): SolNavbarState {
    return this.copy({ initials });
  }

  private recalculateHostClasses(): SolNavbarState {
    let hostClasses = 'lab-navbar-container';
    if (!this.navbarVisible) {
      hostClasses += ' lab-navbar-container-hidden';
    }
    return this.copy({ hostClasses });
  }

  private recalculatePopoverMenus(): SolNavbarState {
    const accountMenu = MenuTools.navbarToMenu(this.account);
    const systemMenus = new Map<string, SolMenuGroup[]>();
    for (const systemItem of this.system) {
      if (systemItem.children.length > 0) {
        systemMenus.set(systemItem.id, MenuTools.navbarToMenu(systemItem.children));
      }
    }
    return this.copy({ accountMenu, systemMenus });
  }

  private copy(props: Partial<SolNavbarState>): SolNavbarState {
    return ObjectTools.copy<SolNavbarState>(this, props);
  }
}
